import { useState, useEffect } from 'react'

interface CountdownTime {
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

const COOKIE_NAME = 'cm_midnight_deadline'

// Next local midnight (tonight's 00:00, i.e. the very start of tomorrow).
function getMidnightTonight(): number {
  const t = new Date()
  t.setHours(24, 0, 0, 0)
  return t.getTime()
}

function setCookie(name: string, value: string, expires: Date) {
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

function getCookie(name: string): string | null {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    const c = ca[i].trim()
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length)
  }
  return null
}

function resolveTarget(): number {
  // Reuse the stored deadline if it's still valid (in the future).
  const stored = getCookie(COOKIE_NAME)
  if (stored) {
    const parsed = parseInt(stored, 10)
    if (!Number.isNaN(parsed) && parsed > Date.now()) {
      return parsed
    }
    // Stored deadline already passed → keep it so the timer stays expired
    // until the cookie itself expires (set to the deadline below).
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  // No (valid) cookie yet → lock in tonight's midnight and persist it.
  const target = getMidnightTonight()
  setCookie(COOKIE_NAME, String(target), new Date(target))
  return target
}

export function useMidnightCountdown(): CountdownTime {
  const compute = (target: number): CountdownTime => {
    const diff = target - Date.now()
    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    return { hours, minutes, seconds, isExpired: false }
  }

  const [timeLeft, setTimeLeft] = useState<CountdownTime>(() => {
    if (typeof document === 'undefined') {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: false }
    }
    return compute(resolveTarget())
  })

  useEffect(() => {
    const target = resolveTarget()
    const tick = () => setTimeLeft(compute(target))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return timeLeft
}
